import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { usePrefs, useRequestData } from 'wsm-common-data';
import { setClassNames } from 'ddc-classnames-js';
import { setNewRelicCustomAttribute } from 'ddc-new-relic';
import { isInTransit } from 'wsm-highlighted-attribute';

import vehiclePropType from '../../../propTypes/vehiclePropType';
import { selectIsListLayout } from '../../../utilities/layout';
import VehicleAttributeItem from './VehicleAttributeItem';

const VehicleAttributeList = ({ vehicle }) => {
	const { layout } = useSelector((state) => state.layout);
	const isListLayout = useSelector((state) => selectIsListLayout(state));
	const {
		commentsEnableOnMobile,
		simplifiedLimitedAttributes,
		showInformationTabs
	} = usePrefs();
	const { deviceType } = useRequestData();
	const commentsDisabled =
		commentsEnableOnMobile !== 'true' && deviceType === 'MOBILE';

	const { attributes = [], status } = vehicle;

	useEffect(() => {
		const hasLocationDistance = attributes.some(
			(attr) => attr.name === 'locationDistance'
		);
		if (hasLocationDistance) {
			setNewRelicCustomAttribute('location-distance-set', true);
		}
	}, [attributes]);

	// The simplifiedLimitedAttributes pref limits which attributes may be displayed in grid layout.
	// Non-numeric values (including '' and 'all') result in no attribute limit.
	// Some grid cards may show fewer than this limit after filtering below - this ensures visual consistency.
	const parsedLimit = simplifiedLimitedAttributes
		? Number(simplifiedLimitedAttributes)
		: NaN;
	const hasAttributeLimit = !isListLayout && !Number.isNaN(parsedLimit);

	if (hasAttributeLimit && parsedLimit === 0) {
		return null;
	}

	let limitedAttributes = attributes;
	if (hasAttributeLimit) {
		limitedAttributes = attributes.slice(0, parsedLimit);
	}

	const renderedAttributes = limitedAttributes
		.filter(({ name, labeledValue }) => {
			switch (name) {
				case 'comments':
					return !commentsDisabled;
				case 'odometer':
					// Show odometer when it is list layout and labeledValue contains actual miles not like just "miles"
					return isListLayout && /\d/.test(labeledValue);
				case 'accountName':
					return !isInTransit(status);
				case 'locationDistance':
				case 'cityStateDistance':
					return false;
				default:
					return true;
			}
		})
		.map(({ name, labeledValue, value, normalizedValue = null }) => {
			const swatchColor =
				normalizedValue && normalizedValue !== 'Other'
					? normalizedValue.toLowerCase()
					: null;
			return (
				<VehicleAttributeItem
					key={name}
					name={name}
					labeledValue={labeledValue}
					value={value}
					swatchColor={swatchColor}
				/>
			);
		});

	if (!renderedAttributes.length) {
		return null;
	}

	return (
		<ul
			className={setClassNames([
				'vehicle-card-description',
				'list-unstyled',
				'w-100',
				deviceType === 'MOBILE' ? 'ddc-font-size-small' : null,
				deviceType !== 'MOBILE' && !isListLayout
					? 'ddc-font-size-xsmall'
					: null,
				layout === 'list' ||
				(!isListLayout &&
					+simplifiedLimitedAttributes &&
					showInformationTabs?.toLowerCase().includes('info'))
					? 'single-col'
					: null
			])}
		>
			{renderedAttributes}
		</ul>
	);
};

VehicleAttributeList.propTypes = {
	vehicle: vehiclePropType.isRequired
};

export default VehicleAttributeList;
